/**
 * refreshCoordinator — single source of truth for token refresh.
 *
 * WHY THIS EXISTS (mobile session-drop bug, 2026-06-28):
 * The backend `/auth/refresh/` endpoint ROTATES the refresh token — every
 * successful refresh revokes the old refresh token and issues a new one
 * (see backend/accounts/token_refresh.py). That makes *concurrent* refreshes
 * fatal: the first call rotates the token, and any other call already in
 * flight is now sending an already-revoked token → 401 → forced logout.
 *
 * Before this module there were THREE independent refreshers, each with its
 * own in-flight flag and none aware of the others:
 *   - api.ts            (ApiService.refreshPromise)
 *   - authInterceptor.ts (isRefreshing + failedQueue)
 *   - tokenRefresh.ts    (proactive setInterval, own isRefreshing)
 * On mobile, returning to the foreground fires a *burst* of requests (all of
 * which 401 on the expired access cookie) PLUS the proactive timer catching
 * up — several refreshes hit the rotating endpoint at once, the losers send a
 * revoked token, and the user is logged out mid-session.
 *
 * This module collapses all of them onto ONE module-level in-flight promise,
 * so no matter how many callers ask to refresh at the same moment, exactly one
 * network refresh happens and everyone awaits its result. The rotation race is
 * eliminated.
 */

import { getApiBaseUrl } from '../utils/urlConfig';

const REFRESH_ENDPOINT = `${getApiBaseUrl().replace(/\/$/, '')}/auth/refresh/`;

export interface RefreshResult {
  ok: boolean;
  status: number;
  data: any;
}

// The single in-flight refresh. While set, every caller awaits this same
// promise instead of firing its own network refresh.
let inFlight: Promise<RefreshResult> | null = null;

function getCSRFToken(): string | null {
  const name = 'csrftoken=';
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const rawCookie of cookies) {
    const cookie = rawCookie.trim();
    if (cookie.startsWith(name)) {
      return decodeURIComponent(cookie.substring(name.length));
    }
  }
  return null;
}

async function doRefresh(maxRetries = 2): Promise<RefreshResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const csrf = getCSRFToken();
  if (csrf) {
    headers['X-CSRFToken'] = csrf;
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(REFRESH_ENDPOINT, {
        method: 'POST',
        credentials: 'include', // send/receive httpOnly auth + refresh cookies
        headers,
        body: '{}',
      });

      // Transient backend hiccup (gateway/restart). Retry with backoff rather
      // than treating it as an auth failure — a 502 must never log the user out.
      if (
        (response.status === 502 || response.status === 503 || response.status === 521) &&
        attempt < maxRetries
      ) {
        await new Promise((r) => setTimeout(r, Math.pow(2, attempt) * 1000));
        continue;
      }

      let data: any = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      return { ok: response.ok, status: response.status, data };
    } catch (error) {
      // Network error (offline, dropped connection). Do NOT retry.
      //
      // This endpoint is NOT idempotent — it rotates the refresh token. A
      // network error is ambiguous in the worst possible way: the request may
      // have reached the server and committed a rotation whose Set-Cookie we
      // never received. Retrying then re-presents the just-revoked token, lands
      // in the server's 60s grace window, and comes back 200 — so a lost
      // rotation was laundered into an apparent success while the cookie jar
      // held dead credentials. That silent "success" is what turned one dropped
      // response into a forced logout minutes later (2026-08-17).
      //
      // CALLER CONTRACT — every consumer of this result MUST treat status 0
      // (and 5xx) as TRANSIENT and keep the session; only an explicit 401/403
      // may trigger a logout (playbook §10). Not retrying here means the throw
      // now actually reaches those callers instead of being absorbed by a retry,
      // so that contract is load-bearing. It is enforced at:
      //   api.ts refreshSession()      — tags `isTransientRefreshFailure`
      //   api.ts (both 401 branches)   — gated before handleAuthFailure()
      //   authInterceptor.ts           — gated on result.status 401/403
      //   authSlice.ts rehydrateAuth   — `status === 0 || >= 500` → retryable
      // If you add a caller, add the guard, or a dropped packet becomes a logout.
      return { ok: false, status: 0, data: { error: (error as Error)?.message || 'Network error' } };
    }
  }

  return { ok: false, status: 0, data: { error: 'Refresh exhausted retries' } };
}

/**
 * Refresh the access token, coordinated across the whole app.
 *
 * If a refresh is already in flight, returns the SAME promise so concurrent
 * callers never trigger a second (rotation-breaking) network refresh.
 */
export function coordinatedRefresh(): Promise<RefreshResult> {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** True while a coordinated refresh is in flight. */
export function isRefreshInFlight(): boolean {
  return inFlight !== null;
}

/**
 * fetch() wrapper for the handful of services that talk to the backend with
 * raw fetch instead of the shared axios instance (api.ts). Those axios calls
 * already self-heal an expired access cookie via refresh-and-retry (see
 * api.ts's response interceptor); plain fetch() call sites had none of that,
 * so on mobile — where the access cookie silently expires while the tab is
 * backgrounded — the very first request after resume would 401 and stay
 * failed until a full page reload. This gives every fetch() caller the same
 * self-healing: on a 401, run the single coordinated refresh, then retry the
 * request exactly once.
 */
export async function fetchWithAuthRetry(url: string, options: RequestInit = {}): Promise<Response> {
  const requestInit: RequestInit = { ...options, credentials: 'include' };
  const response = await fetch(url, requestInit);
  if (response.status !== 401) {
    return response;
  }

  const result = await coordinatedRefresh();
  if (!result.ok) {
    // Refresh failed (or genuinely logged out) — surface the original 401.
    return response;
  }

  return fetch(url, requestInit);
}
